import { useCurrencies } from 'contexts/hooks/useCurrencies'
import useTranslation from 'next-translate/useTranslation'
import { useEffect, useState } from 'react'
import { getTrackBackground, Range as ReactRange } from 'react-range'
import { tw } from '../../utils/strings'

type Values = {
  min: number
  max: number
}

type Props = {
  className?: string
  id?: string
  label?: string | React.ReactNode
  min?: number
  max?: number

  values?: Values
  step?: number
  unit?: string
  currency?: string
  size?: 'sm' | 'md' | 'lg'
  onChange?: (value: Values) => void
}

export const Range = ({
  id,
  label,
  min = 0,
  max = 100,
  values,
  step = 0.1,
  unit,
  currency,
  size = 'md',
  onChange,
  className,
}: Props) => {
  const { t } = useTranslation()
  const [internalValues, setInternalValues] = useState<Values>(
    values || { min, max }
  )
  const { formatCurrency } = useCurrencies()

  useEffect(() => {
    values && setInternalValues(values)
  }, [values])

  const styles: { [key: string]: string } = {
    sm: tw`text-sm h-8 px-3`,
    md: tw`text-sm h-10 px-3`,
    lg: tw`text-sm h-12 px-3`,

    'thumb-sm': tw`h-4 w-4`,
    'thumb-md': tw`h-5 w-5`,
    'thumb-lg': tw`h-6 w-6`,

    'indicator-sm': tw`mb-1 text-xs`,
    'indicator-md': tw`mb-1 text-sm`,
    'indicator-lg': tw`mb-2 text-sm`,
  }

  const formatMinValueZero = currency
    ? `${currency}${internalValues.min}`
    : internalValues.min

  const formatMinValueNotZero = currency
    ? `${formatCurrency(internalValues.min)}`
    : `${internalValues.min}`

  return (
    <div className={`w-full ${className}`}>
      {label && (
        <label
          className='mb-1 flex justify-between text-sm font-medium'
          htmlFor={id}
        >
          {label}
        </label>
      )}

      <div
        className={`relative flex w-full flex-col items-center justify-center ${styles[size]}`}
      >
        <p className={`text-cool-600 ${styles['indicator-' + size]}`}>
          <span translate='no'>
            {internalValues.min === 0
              ? formatMinValueZero
              : formatMinValueNotZero}
          </span>{' '}
          {t('common:to')}{' '}
          <span translate='no'>
            {currency ? formatCurrency(internalValues.max) : internalValues.max}
          </span>
          {internalValues.max === 10000 ? '+' : ''} {unit}
        </p>
        <ReactRange
          values={[internalValues.min, internalValues.max]}
          step={step}
          min={min}
          max={max}
          onChange={newValues => {
            setInternalValues({ min: newValues[0], max: newValues[1] })
            onChange && onChange({ min: newValues[0], max: newValues[1] })
          }}
          renderTrack={({ props, children }) => {
            return (
              <div
                style={{ ...props.style }}
                onMouseDown={props.onMouseDown}
                onTouchStart={props.onTouchStart}
                className='w-full'
              >
                <div
                  ref={props.ref}
                  className='h-[3px] w-full self-center rounded-full'
                  style={{
                    background: getTrackBackground({
                      values: [internalValues.min, internalValues.max],
                      colors: ['#D9DBE9', '#4E4B66', '#D9DBE9'],
                      min,
                      max,
                    }),
                  }}
                >
                  {children}
                </div>
              </div>
            )
          }}
          renderThumb={({ props, isDragged }) => {
            return (
              <div
                {...props}
                style={{ ...props.style }}
                className='outline-none'
              >
                <div
                  className={`rounded-full border border-cool-400 transition-all duration-200 hover:bg-primary-default
                    ${styles['thumb-' + size]}
                    ${isDragged ? 'bg-primary-default' : 'bg-cool-150'}
                  `}
                />
              </div>
            )
          }}
        />
      </div>
    </div>
  )
}
